import { AutomationDeliveryProvider } from "@repo/app-types/entities/automation";
import { IntegrationProvider } from "@repo/app-types/entities/integration";
import { automationDeliveryProviderLabels } from "@repo/ui/automations/automationPresentation";
import { IconMail } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import type { RootState } from "../../app/store.js";
import slackLogoSrc from "../../assets/integrations/slackLogo.png";
import teamsLogoSrc from "../../assets/integrations/teamsLogo.png";
import {
  selectSlackIntegration,
  selectTeamsIntegration,
} from "../integrations/integrationsSlice.js";

/**
 * Everything the delivery UI needs to know about one provider, so
 * NotificationDeliveryFields stays provider-agnostic
 */
export interface DeliveryProviderDefinition {
  id: AutomationDeliveryProvider;
  label: string;
  /**
   * The radio label when each person gets their own result privately, so the
   * option names the delivery that result actually gets — a Slack DM rather
   * than "Slack", which would suggest a channel is on the table.
   */
  directMessageLabel: string;
  renderIcon: () => ReactNode;
  /**
   * How to reach the provider's connected integration; undefined when the
   * provider needs none (email). While no integration is connected the
   * delivery UI shows a Connect CTA that runs the OAuth flow for
   * `oauthProvider`.
   */
  connection?: {
    select: (state: RootState) => { id: string } | null;
    oauthProvider: IntegrationProvider;
  };
  /**
   * The destination chooser, with one sub-object per kind of
   * `AutomationChatDeliveryMode`. Undefined (email) means the destination
   * is fixed to private per-recipient delivery, so the section renders the
   * recipient controls directly.
   */
  destinationConfig?: {
    channel: {
      label: string;
      pickerPlaceholder: string;
      /** Lists the pickable channels with display-ready names. */
      list: (
        integrationId: string,
      ) => Promise<readonly { id: string; name: string }[]>;
    };
    directMessage: {
      label: string;
      /** Shown instead of the radio when a template only allows private delivery. */
      explanation: string;
    };
  };
}

export const deliveryProviderDefinitions: Record<
  AutomationDeliveryProvider,
  DeliveryProviderDefinition
> = {
  [AutomationDeliveryProvider.EMAIL]: {
    id: AutomationDeliveryProvider.EMAIL,
    label: automationDeliveryProviderLabels[AutomationDeliveryProvider.EMAIL],
    directMessageLabel:
      automationDeliveryProviderLabels[AutomationDeliveryProvider.EMAIL],
    renderIcon: () => (
      <IconMail aria-hidden className="size-4 text-muted-foreground" />
    ),
  },
  [AutomationDeliveryProvider.SLACK]: {
    id: AutomationDeliveryProvider.SLACK,
    label: automationDeliveryProviderLabels[AutomationDeliveryProvider.SLACK],
    directMessageLabel: "Slack DM",
    renderIcon: () => (
      <img
        alt=""
        aria-hidden="true"
        className="size-4 shrink-0 object-contain"
        src={slackLogoSrc}
      />
    ),
    connection: {
      select: selectSlackIntegration,
      oauthProvider: IntegrationProvider.SLACK,
    },
    destinationConfig: {
      channel: {
        label: "Channel",
        pickerPlaceholder: "Search Slack channels...",
        list: async (integrationId) => {
          const axios = await getAxiosInstanceWithAuth();
          const { data } = await axios.get(`/slack/${integrationId}/channels`);
          return (data as { id: string; name: string }[]).map((channel) => ({
            id: channel.id,
            name: `# ${channel.name}`,
          }));
        },
      },
      directMessage: {
        label: "Direct messages",
        explanation:
          "Each selected person gets a direct message, matched by their email.",
      },
    },
  },
  [AutomationDeliveryProvider.TEAMS]: {
    id: AutomationDeliveryProvider.TEAMS,
    label: automationDeliveryProviderLabels[AutomationDeliveryProvider.TEAMS],
    directMessageLabel: "Microsoft Teams chat",
    renderIcon: () => (
      <img
        alt=""
        aria-hidden="true"
        className="size-4 shrink-0 object-contain"
        src={teamsLogoSrc}
      />
    ),
    connection: {
      select: selectTeamsIntegration,
      oauthProvider: IntegrationProvider.TEAMS,
    },
    destinationConfig: {
      channel: {
        label: "Channel",
        pickerPlaceholder: "Search Teams channels...",
        list: async (integrationId) => {
          const axios = await getAxiosInstanceWithAuth();
          const { data } = await axios.get(`/teams/${integrationId}/channels`);
          // A channel name alone is ambiguous across teams, so the pickable
          // (and stored) name carries its team.
          return (
            data as { id: string; name: string; team_name: string }[]
          ).map((channel) => ({
            id: channel.id,
            name: `${channel.team_name} › ${channel.name}`,
          }));
        },
      },
      directMessage: {
        label: "Direct messages",
        explanation:
          "Each selected person gets a direct message, matched by their email.",
      },
    },
  },
};

export const allDeliveryProviderDefinitions: readonly DeliveryProviderDefinition[] =
  Object.values(deliveryProviderDefinitions);

export function checkIsDeliveryProvider(
  value: string,
): value is AutomationDeliveryProvider {
  return value in deliveryProviderDefinitions;
}
